import type {
    DataTableColumn,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { StatusChip } from '@/components/ui/info-grid'
import CheckIcon from '@/icons/CheckIcon'
import {
    CONTRACT_DIRECTION_OPTIONS,
    CONTRACT_ROLE_OPTIONS,
} from '@/modules/Contract/constants'
import { getAccountManagerOptionsPaginated } from '@/modules/Employee/services'
import EInvoiceStatusCell from '@/modules/Invoice/components/List/EInvoiceStatusCell'
import SignedDocumentStatusCell from '@/modules/Invoice/components/List/SignedDocumentStatusCell'
import {
    E_INVOICE_FILTER_OPTIONS,
    INVOICE_STATUS,
    SIGNED_DOCUMENT_FILTER_OPTIONS,
    getInvoicableDecision,
    INVOICABLE_DECISIONS,
} from '@/modules/Invoice/constants'
import {
    SIGNED_DOCUMENT_TYPES,
    SIGNED_DOCUMENTS,
} from '@/modules/Invoice/constants/signed-documents'
import { GetInvoicesRow } from '@/modules/Invoice/types'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { generatePathDatacentral } from '@/utils/path'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'

// ============================================================================
// Column Configuration
// ============================================================================

/**
 * Column configuration for Invoice List table
 * Sortable fields: invoice_id, invoice_status, payment_request_status, reconciliation_statement_status,
 * e_invoice_status (khi showEInvoice),
 * invoice_total, invoice_duedate,
 * invoice_apply_startdate, invoice_apply_enddate, invoice_payment_at
 */
export interface InvoiceListConfigOptions {
    /** Cột / filter "HĐ điện tử": chỉ nhóm Kế toán / BOM / BOD / Admin (useEInvoiceAccess().canView) */
    showEInvoice?: boolean
}

/** Cột "HĐ điện tử" (sort: chưa phát hành → failed → publishing → published → wrong) */
const getEInvoiceColumn = (t: TFunction): DataTableColumn<GetInvoicesRow> => ({
    key: 'e_invoice_status',
    title: t('e-invoice-column'),
    width: 180,
    sortable: true,
    render: (_, row) => <EInvoiceStatusCell row={row} />,
})

export const getInvoiceListColumns = (
    t: TFunction,
    { showEInvoice = false }: InvoiceListConfigOptions = {}
): DataTableColumn<GetInvoicesRow>[] => {
    const columns = buildInvoiceListColumns(t)
    if (!showEInvoice) return columns
    // Đặt sau các cột chứng từ ký số
    const index = columns.findIndex(
        (column) => column.key === 'reconciliation_statement_status'
    )
    columns.splice(index + 1, 0, getEInvoiceColumn(t))
    return columns
}

const buildInvoiceListColumns = (
    t: TFunction
): DataTableColumn<GetInvoicesRow>[] => [
    {
        key: 'invoice_id',
        title: t('invoice_id'),
        width: 100,
        sortable: true,
        formatter: createInternalLinkFormatter({
            pathKey: 'invoiceDetail',
            pathParams: (value) => ({ id: value }),
        }),
        pinned: 'left',
    },
    {
        key: 'invoice_exporter_code',
        title: t('invoice_exporter_code'),
        width: 150,
        sortable: false,
        render: (_, row) => {
            if (!row?.invoice_exporter_code) {
                return <span className="text-gray-400">---</span>
            }
            return <span>{row.invoice_exporter_code}</span>
        },
    },
    {
        key: 'email_status',
        title: t('email_status'),
        width: 120,
        sortable: false,
        cellClassName: 'text-center',
        render: (_, row) => {
            const mailCount = row?.invoice_mail_count || 0
            if (mailCount === 0) {
                return <span className="text-gray-400">-</span>
            }
            const checksToShow = Math.min(mailCount, 5)
            return (
                <div className="flex items-center justify-center -space-x-2.5">
                    {Array.from({ length: checksToShow }).map((_, idx) => (
                        <CheckIcon
                            key={idx}
                            className="w-3.5 h-3.5 text-green-600"
                        />
                    ))}
                </div>
            )
        },
    },
    // Trạng thái chứng từ ký số: cột "Đề nghị TT", "Biên bản"
    ...SIGNED_DOCUMENT_TYPES.map(
        (documentType): DataTableColumn<GetInvoicesRow> => ({
            key: SIGNED_DOCUMENTS[documentType].statusField,
            title: t(`${SIGNED_DOCUMENTS[documentType].i18nKey}.column`),
            width: 150,
            // BE sort: chưa gửi → failed → pending → signed
            sortable: true,
            cellClassName: 'text-center',
            render: (_, row) => (
                <SignedDocumentStatusCell
                    row={row}
                    documentType={documentType}
                />
            ),
        })
    ),
    {
        key: 'customer_name',
        title: t('customer_name'),
        width: 350,
        cellClassName: 'truncate',
        sortable: false,
        render: (_, row) => {
            const customerName =
                row?.customer?.company_name || row?.customer?.customer_name
            const customerId = row?.customer?.customer_id
            return (
                <Link
                    to={
                        generatePathDatacentral('/customers/:id', {
                            id: customerId,
                        }) + '?tab=invoices'
                    }
                    className="text-primary hover:text-primary-hover hover:underline uppercase"
                >
                    {customerId} - {customerName}
                </Link>
            )
        },
    },
    {
        key: 'service',
        title: t('service'),
        width: 350,
        sortable: false,
        cellClassName: 'truncate',
        render: (_, row) => {
            if (row?.invoice_type === 'deposit') {
                return <span className="uppercase">{t('deposit')}</span>
            }
            if (!row?.service?.service_id) {
                return <span className="text-gray-400">-</span>
            }
            return (
                <Link
                    to={generatePathDatacentral('/services/:id', {
                        id: row?.service?.service_id,
                    })}
                    className="hover:underline uppercase block truncate"
                    title={`${row?.service?.service_id} - ${row?.service?.service_name}`}
                >
                    {row?.service?.service_id} - {row?.service?.service_name}
                </Link>
            )
        },
    },
    {
        key: 'invoice_status',
        title: t('invoice_status'),
        width: 200,
        sortable: true,
        // "Đã duyệt" chưa nói CS đã chốt cần xuất hoá đơn hay chưa: hiện kèm quyết định đó
        render: (_, row) => {
            const decision = getInvoicableDecision(row)
            return (
                <div className="flex flex-col items-start gap-1">
                    <StatusChip
                        value={row.invoice_status}
                        options={INVOICE_STATUS(t)}
                    />
                    {decision && (
                        <StatusChip
                            value={decision}
                            options={INVOICABLE_DECISIONS(t)}
                        />
                    )}
                </div>
            )
        },
    },
    {
        key: 'invoice_sale_type',
        title: t('invoice_sale_type'),
        width: 150,
        sortable: false,
        render: (value) => {
            if (!value) return <span className="text-gray-400">---</span>
            const saleTypeMap: Record<string, string> = {
                'new-sell': t('new_sale'),
                renewable: t('renewable'),
                upsell: t('upsale'),
            }
            return (
                <span className="uppercase">{saleTypeMap[value] || value}</span>
            )
        },
    },
    {
        key: 'contract_role',
        title: t('contract_role'),
        width: 160,
        sortable: false,
        formatter: createOptionsFormatter({
            options: CONTRACT_ROLE_OPTIONS(t) as OptionItem[],
        }),
    },
    {
        key: 'contract_direction',
        title: t('contract_direction'),
        width: 140,
        sortable: false,
        formatter: createOptionsFormatter({
            options: CONTRACT_DIRECTION_OPTIONS(t) as OptionItem[],
        }),
    },
    {
        key: 'account_manager_names',
        title: t('account_manager_names'),
        width: 180,
        sortable: false,
        render: (_, row) => {
            if (!row?.customer?.account_manager_names?.length)
                return (
                    <span className="text-gray-400 italic">
                        {t('unassigned')}
                    </span>
                )
            return (
                <span>
                    {row?.customer?.account_manager_names?.join(', ') || ''}
                </span>
            )
        },
    },
    {
        key: 'invoice_total',
        title: t('invoice_total'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({
            currency: '',
            decimals: 0,
        }),
    },
    {
        key: 'invoice_note',
        title: t('invoice_note'),
        width: 400,
        sortable: false,
        render: (_, row) => {
            return (
                <span className="uppercase">
                    {row?.invoice_description || ''}
                </span>
            )
        },
    },
    {
        key: 'invoice_apply_startdate',
        title: t('invoice_apply_startdate'),
        width: 130,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },
    {
        key: 'invoice_apply_enddate',
        title: t('invoice_apply_enddate'),
        width: 130,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },
    {
        key: 'invoice_duedate',
        title: t('invoice_duedate'),
        width: 130,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },
    {
        key: 'invoice_vat',
        title: t('invoice_vat'),
        width: 140,
        sortable: false,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({
            currency: '',
            decimals: 0,
            hideZero: true,
        }),
    },
    {
        key: 'invoice_amount',
        title: t('invoice_amount'),
        width: 140,
        sortable: false,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({
            currency: '',
            decimals: 0,
        }),
    },
    {
        key: 'invoice_payment_at',
        title: t('invoice_payment_at'),
        width: 130,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({
            format: 'DD/MM/YYYY',
        }),
    },
    {
        key: 'email_count',
        title: t('email_count'),
        width: 100,
        sortable: false,
        render: (_, row) => {
            return (
                <span>
                    {row?.invoice_mail_count || 0} {t('times')}
                </span>
            )
        },
    },
]

// ============================================================================
// Filter Items Config
// ============================================================================

/**
 * Filter configuration for Invoice List
 * Supports status, product group, account manager, and date range filters
 */
export const getInvoiceListFilterItems = (
    t: TFunction,
    setFilter?: <K extends string>(key: K, value: any) => void,
    { showEInvoice = false }: InvoiceListConfigOptions = {}
): FilterItemConfig[] => {
    /**
     * Fetch product group options for async-select filter
     */
    const getProductGroupOptions = async (query: string) => {
        const allOptions = await getProductsGroupOptions()

        if (!allOptions || allOptions.length === 0) {
            return { options: [], hasMore: false }
        }

        // Client-side filtering based on search query
        const options = query
            ? allOptions.filter((opt) =>
                  opt.label.toLowerCase().includes(query.toLowerCase())
              )
            : allOptions

        return { options, hasMore: false }
    }

    return [
        {
            name: 'invoice_status',
            type: 'multi-select',
            label: t('invoice_status'),
            placeholder: t('select-status'),
            options: INVOICE_STATUS(t),
        },
        // Filter trạng thái chứng từ ký số ('none' = chưa gửi), trong JSON base64 `filters`
        ...SIGNED_DOCUMENT_TYPES.map(
            (documentType): FilterItemConfig => ({
                name: SIGNED_DOCUMENTS[documentType].statusField,
                type: 'multi-select',
                label: t(`${SIGNED_DOCUMENTS[documentType].i18nKey}.column`),
                placeholder: t(
                    `${SIGNED_DOCUMENTS[documentType].i18nKey}.column`
                ),
                options: SIGNED_DOCUMENT_FILTER_OPTIONS(t),
            })
        ),
        // Filter hoá đơn điện tử ('none' = chưa phát hành), trong JSON base64 `filters`
        ...(showEInvoice
            ? [
                  {
                      name: 'e_invoice_status',
                      type: 'multi-select',
                      label: t('e-invoice-column'),
                      placeholder: t('e-invoice-column'),
                      options: E_INVOICE_FILTER_OPTIONS(t),
                  } satisfies FilterItemConfig,
              ]
            : []),
        {
            name: 'contract_role',
            type: 'multi-select',
            label: t('contract_role'),
            placeholder: t('contract_role'),
            options: CONTRACT_ROLE_OPTIONS(t),
        },
        {
            name: 'contract_direction',
            type: 'multi-select',
            label: t('contract_direction'),
            placeholder: t('contract_direction'),
            options: CONTRACT_DIRECTION_OPTIONS(t),
        },
        {
            name: 'product_group_name',
            type: 'async-select',
            label: t('product_group_name'),
            placeholder: t('select-product-group'),
            asyncOptions: getProductGroupOptions,
            queryKey: 'product-groups',
            multiple: true,
        },
        {
            name: 'account_manager_codes',
            type: 'async-select',
            label: t('account_manager_codes'),
            placeholder: t('select-account-manager'),
            asyncOptions: async (query, page) => {
                const result = await getAccountManagerOptionsPaginated(
                    query,
                    page
                )
                if (page === 1 && !query) {
                    const noAssigneeOption = {
                        label: t('no-cs-assignee'),
                        value: '-1',
                    }
                    return {
                        ...result,
                        options: [noAssigneeOption, ...result.options],
                    }
                }
                return result
            },
            queryKey: 'account-managers',
            multiple: true,
        },
        {
            name: 'invoice_apply_startdate',
            type: 'daterangepicker',
            label: t('invoice_apply_date'),
            placeholder: t('select-date-range'),
            names: ['invoice_apply_startdate', 'invoice_apply_startdate_to'],
            showPresets: true,
            onChange: setFilter
                ? (val: [string, string]) => {
                      const arrayVal = val[0] ? [val[0], val[1]] : []
                      setFilter('invoice_apply_startdate', arrayVal)
                      setFilter('invoice_apply_enddate', arrayVal)
                  }
                : undefined,
        },
    ]
}
